<template>
  <signup-layout>
    <p class="header-text">Create Your Account</p>
    <img
      class="-ml-3 w-full"
      src="@/assets/tracker-signup.svg"
      alt="sign up tracker"
    />
    <form
      novalidate="novalidate"
      @submit.prevent="handleSubmit"
    >
      <div>
        <p class="mt-6 text-1lg text-dark-blue">Verification</p>
        <p class="my-6 text-left text-sm text-dark-gray sm:my-8">
          Enter the code we just sent to the mobile number you entered.
        </p>
        <form-field
          label="Verification Code"
          :value="phone_twilio_validation"
          placeholder="Enter Verification Code"
          :on-input="onInput"
          name="phone_twilio_validation"
          :is-valid="!error"
          error-message="Incorrect verification code"
        />
        <div class="flex-col sm:flex">
          <button-main
            class="my-4 mb-4"
            :disabled="!phone_twilio_validation"
            type="submit"
            value="Continue"
          >
            Continue
          </button-main>
          <div class="mt-2 text-center sm:mt-4">
            <p class="mb-2 text-1md text-body-color">Code didn't send?</p>
            <p
              class="cursor-pointer text-1md text-yellow-500 sm:mb-4"
              @click="() => createVerification()"
            >
              Try Again
            </p>
          </div>
        </div>
      </div>
    </form>
  </signup-layout>
</template>

<script type="text/javascript">
import { toastResponse } from '@/common/handleServerResponse';
import ButtonMain from '@/components/Button.vue';
import FormField from '@/components/FormField.vue';
import SignupLayout from '@/components/layouts/SignupLayout.vue';

// import {
//   formatPhoneParenthesis,
//   formatFirstLastName,
// } from "@/common/validators";

export default {
  name: 'My Profile Verification',
  components: {
    ButtonMain,
    FormField,
    SignupLayout,
  },
  data() {
    return {
      error: false,
      phone_twilio_validation: '',
    };
  },
  computed: {
    isPrivacyPolicyConsentedAt() {
      return (
        this.$store.state.users.currentUser?.privacy_policy_consented_at
          ?.length > 0 || false
      );
    },
    shouldShowPrivacyPolicy() {
      return (
        this.isPrivacyPolicyStepEnabled &&
        !this.isPrivacyPolicyConsentedAt &&
        !this.displayPrivacyPolicyStep
      );
    },
    isPrivacyPolicyStepEnabled() {
      return this.$store.getters['users/getFeatureFlag']?.(
        'privacy_policy_step_enabled'
      )?.['enabled'];
    },
    // currentUser(){
    //   return this.$store.state.users.currentUser;
    // },
    // needsPhone(){
    //   return this.$store.state.users.currentUser?.phone_e164 == null;
    // },
    // needsName(){
    //   return this.$store.state.users.currentUser?.first_name == null;
    // },
    currentVerification() {
      return this.$store.state.verifications.currentVerification;
    },
    phoneNumber() {
      return this.$store.state.signUpData.phone_number;
    },
  },
  mounted() {
    this.displayPrivacyPolicyStep =
      localStorage.getItem('showPrivacyPolicyStep') === 'true';
  },
  methods: {
    onInput(e) {
      this.phone_twilio_validation = e.target.value;
    },
    async handleSubmit() {
      const res = await this.$store.dispatch(
        'verifications/confirmVerification',
        {
          code: this.phone_twilio_validation,
          verification_id: this.currentVerification.id,
        }
      );

      if ([200, 201].includes(res.status)) {
        // verification code is valid
        // run check for open user invitation
        this.validation_error = false;
        const userUpdateResult = await this.$store.dispatch(
          'users/updatePhoneWithVerification',
          this.currentVerification.id
        );

        await this.$store.dispatch(
          'users/getFeatureFlags',
          userUpdateResult.data.id
        );

        if (userUpdateResult.status == 200) {
          let redirectAfterSignIn = JSON.parse(
            localStorage.getItem('redirect_url_after_sigin')
          );

          // If they came in through the OAuth process,
          // then then they will be redirected to the /oauth/authorizations
          // endpoint with the correct credentials

          const sendToIDV = this.$store.getters['users/getFeatureFlag']?.(
            'idv_onboarding_flow'
          )?.enabled;

          let fallbackRouteName = 'Home';

          if (sendToIDV) {
            fallbackRouteName = 'IdentityVerification';
          } else {
            // 2. Fallback based on company type
            const companyType =
              this.$store.state.users.currentUser.company.company_type;

            if (companyType === 'broker')
              fallbackRouteName = 'Broker Dashboard';
            else if (companyType === 'carrier')
              fallbackRouteName = 'CarrierWelcomePage';
            else if (companyType === 'dispatcher')
              fallbackRouteName = 'Dispatcher Dashboard';
          }
          // Case 1: `redirectAfterSignIn` exists and policies are verified
          if (redirectAfterSignIn && !this.shouldShowPrivacyPolicy) {
            console.log(
              'Redirecting to redirectAfterSignIn, policies verified'
            );
            localStorage.removeItem('redirect_url_after_sigin'); // Remove item after use
            return this.$router.push(redirectAfterSignIn);
          }

          // Case 2: `redirectAfterSignIn` exists but policies are NOT verified
          if (redirectAfterSignIn && this.shouldShowPrivacyPolicy) {
            console.log('Redirecting to PrivacyPolicy for verification');
            return this.$router.push({
              name: 'PrivacyPolicy',
              query: {
                redirectTo: sendToIDV ? 'IdentityVerification' : undefined,
              },
            });
          }

          // Case 3: `redirectAfterSignIn` does NOT exist and policies are NOT verified
          if (!redirectAfterSignIn && this.shouldShowPrivacyPolicy) {
            console.log('Redirecting to PrivacyPolicy with fallback query');
            return this.$router.push({
              name: 'PrivacyPolicy',
              query: { redirectTo: fallbackRouteName }, // Use `fallbackRoute` as `redirectTo`
            });
          }

          // Case 4: `redirectAfterSignIn` does NOT exist and policies are already verified
          console.log('Redirecting to fallback route, policies verified');
          return this.$router.push({
            name: sendToIDV ? 'IdentityVerification' : fallbackRouteName,
          });
        } else {
          toastResponse(this, res, null);
        }
      } else if (res.status === 422) {
        this.validation_error = true;
        toastResponse(this, res, null);
      } else {
        this.validation_error = false;
        toastResponse(this, res, null);
      }
    },
    async createVerification() {
      const res = await this.$store.dispatch(
        'verifications/createVerification',
        this.phone_number
      );
      if (![200, 201].includes(res.status)) {
        toastResponse(this, res, null);
      }
    },
  },
};
</script>
<style scoped>
.header-text {
  font-size: 26px;
  @apply mb-6 text-dark-blue md:mb-8;
}
</style>
